import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import moment from "moment-timezone";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import "./CSS/CustomerCreditPremium.css";
import "./CSS/DiscountPremium.css";
import {
  FaCheckCircle,
  FaEdit,
  FaExclamationCircle,
  FaFileExport,
  FaFilter,
  FaLayerGroup,
  FaPlus,
  FaRedoAlt,
  FaSave,
  FaSearch,
  FaTimes,
  FaTrashAlt,
  FaTags,
  FaWallet,
} from "react-icons/fa";

const pageSize = 5;
const indiaNow = () => moment().tz("Asia/Kolkata");
const defaultRange = {
  startDate: indiaNow().startOf("month").format("YYYY-MM-DDT00:00"),
  endDate: indiaNow().endOf("day").format("YYYY-MM-DDT23:59"),
};

const defaultRule = {
  id: null,
  name: "",
  product_scope: "fuel",
  fuel_type: "",
  discount_type: "flat",
  discount_value: "",
  min_amount: "",
  min_volume: "",
  max_discount_amount: "",
  approval_required: false,
  auto_apply: false,
  start_date: "",
  end_date: "",
  is_active: true,
};

const defaultTxn = {
  id: null,
  rule_id: "",
  reconciliation_id: "",
  operator_id: "",
  customer_name: "",
  vehicle_no: "",
  product_type: "",
  quantity: "",
  gross_amount: "",
  discount_amount: "",
  status: "posted",
  approval_note: "",
  approved_by: "",
  discount_time: indiaNow().format("YYYY-MM-DDTHH:mm"),
  remark: "",
};

function money(value) {
  return `Rs ${Number(value || 0).toFixed(2)}`;
}

function parseSafeDate(value) {
  if (!value) return null;
  const d = moment(value);
  return d.isValid() ? d : null;
}

function NotificationToast({ notice, onClose }) {
  if (!notice) return null;

  const Icon =
    notice.type === "success"
      ? FaCheckCircle
      : notice.type === "warning" || notice.type === "error"
      ? FaExclamationCircle
      : FaWallet;

  return (
    <div
      className={`cc-toast ${notice.type}`}
      role={notice.type === "warning" || notice.type === "error" ? "alert" : "status"}
      aria-live={notice.type === "warning" || notice.type === "error" ? "assertive" : "polite"}
    >
      <div className="cc-toast-icon">
        <Icon aria-hidden="true" />
      </div>
      <div className="cc-toast-copy">
        <strong>{notice.title}</strong>
        <span>{notice.message}</span>
      </div>
      <button className="cc-toast-close" type="button" onClick={onClose} aria-label="Close">
        <FaTimes aria-hidden="true" />
      </button>
    </div>
  );
}

function StatCard({ tone, icon: Icon, label, value, caption }) {
  return (
    <article className={`cc-summary-card ${tone}`}>
      <div className="cc-summary-icon">
        <Icon aria-hidden="true" />
      </div>
      <div className="cc-summary-copy">
        <span>{label}</span>
        <strong>{value}</strong>
        <em>{caption}</em>
      </div>
    </article>
  );
}

function ConfirmModal({ open, title, message, onCancel, onConfirm }) {
  if (!open) return null;

  return (
    <div className="discount-modal-overlay" role="presentation">
      <div className="discount-modal" role="dialog" aria-modal="true" aria-labelledby="discount-modal-title">
        <div className="discount-modal-head">
          <div className="discount-modal-icon">
            <FaTrashAlt aria-hidden="true" />
          </div>
          <div>
            <span>Confirm action</span>
            <h3 id="discount-modal-title">{title}</h3>
          </div>
        </div>
        <p>{message}</p>
        <div className="discount-modal-actions">
          <button type="button" className="btn btn-light" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="btn btn-danger" onClick={onConfirm}>
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

function getPageNumbers(activePage, totalPages) {
  if (totalPages <= 1) return [1];
  const pages = new Set([1, totalPages, activePage, activePage - 1, activePage + 1]);
  return Array.from(pages)
    .filter((page) => page >= 1 && page <= totalPages)
    .sort((a, b) => a - b);
}

function discountFormula(rule, gross, quantity) {
  if (!rule) return 0;
  const type = String(rule.discount_type || "flat").toLowerCase();
  const value = Number(rule.discount_value || 0);
  let amount = 0;

  if (type === "percent") {
    amount = (gross * value) / 100;
  } else if (type === "per_liter") {
    amount = quantity * value;
  } else {
    amount = value;
  }

  const cap = rule.max_discount_amount !== null && rule.max_discount_amount !== "" ? Number(rule.max_discount_amount) : null;
  if (Number.isFinite(cap) && cap >= 0) {
    amount = Math.min(amount, cap);
  }
  return Math.max(0, Number(amount || 0));
}

export default function DiscountManagement() {
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const [tab, setTab] = useState("rules");
  const [notice, setNotice] = useState(null);
  const [loadingRules, setLoadingRules] = useState(false);
  const [loadingTxn, setLoadingTxn] = useState(false);
  const [savingRule, setSavingRule] = useState(false);
  const [savingTxn, setSavingTxn] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [rulePage, setRulePage] = useState(1);
  const [txnPage, setTxnPage] = useState(1);

  const [rules, setRules] = useState([]);
  const [ruleForm, setRuleForm] = useState(defaultRule);
  const [ruleSearch, setRuleSearch] = useState("");
  const [ruleScopeFilter, setRuleScopeFilter] = useState("");
  const [ruleStatusFilter, setRuleStatusFilter] = useState("all");

  const [transactions, setTransactions] = useState([]);
  const [txnForm, setTxnForm] = useState(defaultTxn);
  const [txnSearch, setTxnSearch] = useState("");
  const [txnStatusFilter, setTxnStatusFilter] = useState("");
  const [txnOperatorFilter, setTxnOperatorFilter] = useState("");
  const [txnRange, setTxnRange] = useState(defaultRange);
  const [users, setUsers] = useState([]);

  const [filters, setFilters] = useState({
    ...defaultRange,
    operator_id: "",
    status: "",
    search: "",
  });

  const showNotice = (type, title, message) => {
    setNotice({ type, title, message });
    window.clearTimeout(showNotice._timer);
    showNotice._timer = window.setTimeout(() => setNotice(null), 3600);
  };

  const fetchUsers = async () => {
    try {
      const res = await api.get("/api/users", { headers });
      setUsers(res.data || []);
    } catch (err) {
      showNotice("warning", "Operators unavailable", "Could not load operator list.");
    }
  };

  const fetchRules = async () => {
    setLoadingRules(true);
    try {
      const res = await api.get("/api/discounts/rules", {
        headers,
        params: {
          is_active: ruleStatusFilter === "all" ? "" : ruleStatusFilter === "active" ? 1 : 0,
          product_scope: ruleScopeFilter || "",
          search: ruleSearch.trim() || "",
        },
      });
      setRules(res.data || []);
    } catch (err) {
      showNotice("error", "Rule load failed", err?.response?.data?.error || "Unable to load discount rules.");
    } finally {
      setLoadingRules(false);
    }
  };

  const fetchTransactions = async (nextFilters = filters) => {
    setLoadingTxn(true);
    try {
      const res = await api.get("/api/discounts/transactions", {
        headers,
        params: nextFilters,
      });
      setTransactions(res.data?.rows || []);
    } catch (err) {
      showNotice("error", "Entry load failed", err?.response?.data?.error || "Unable to load discount entries.");
    } finally {
      setLoadingTxn(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchRules();
    fetchTransactions();
  }, []);

  useEffect(() => {
    fetchRules();
  }, [ruleSearch, ruleScopeFilter, ruleStatusFilter]);

  const filteredRules = useMemo(() => rules, [rules]);
  const filteredRuleCount = filteredRules.length;
  const ruleTotalPages = Math.max(1, Math.ceil(filteredRuleCount / pageSize));
  const safeRulePage = Math.min(rulePage, ruleTotalPages);
  const ruleRows = filteredRules.slice((safeRulePage - 1) * pageSize, safeRulePage * pageSize);

  const currentRule = useMemo(
    () => rules.find((r) => String(r.id) === String(txnForm.rule_id)) || null,
    [rules, txnForm.rule_id]
  );

  const filteredTxnRows = useMemo(() => {
    const q = txnSearch.trim().toLowerCase();
    return transactions.filter((row) => {
      const matchesSearch =
        !q ||
        [row.rule_name, row.operator_name, row.customer_name, row.vehicle_no, row.product_type, row.remark]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(q));
      const matchesStatus = !txnStatusFilter || String(row.status || "").toLowerCase() === txnStatusFilter.toLowerCase();
      const matchesOperator = !txnOperatorFilter || String(row.operator_id) === String(txnOperatorFilter);
      return matchesSearch && matchesStatus && matchesOperator;
    });
  }, [transactions, txnSearch, txnStatusFilter, txnOperatorFilter]);

  const txnTotalPages = Math.max(1, Math.ceil(filteredTxnRows.length / pageSize));
  const safeTxnPage = Math.min(txnPage, txnTotalPages);
  const txnRows = filteredTxnRows.slice((safeTxnPage - 1) * pageSize, safeTxnPage * pageSize);

  useEffect(() => {
    setRulePage(1);
  }, [ruleSearch, ruleScopeFilter, ruleStatusFilter]);

  useEffect(() => {
    setTxnPage(1);
  }, [txnSearch, txnStatusFilter, txnOperatorFilter, txnRange.startDate, txnRange.endDate]);

  const totals = useMemo(() => {
    return filteredTxnRows.reduce(
      (acc, row) => {
        acc.gross += Number(row.gross_amount || 0);
        acc.discount += Number(row.discount_amount || 0);
        acc.net += Number(row.net_amount || 0);
        return acc;
      },
      { gross: 0, discount: 0, net: 0 }
    );
  }, [filteredTxnRows]);

  const ruleSummary = useMemo(() => {
    const active = rules.filter((row) => Number(row.is_active || 0) === 1).length;
    const autoApply = rules.filter((row) => Number(row.auto_apply || 0) === 1).length;
    const approvalRequired = rules.filter((row) => Number(row.approval_required || 0) === 1).length;
    return { active, autoApply, approvalRequired };
  }, [rules]);

  const txnSummary = useMemo(() => {
    const posted = filteredTxnRows.filter((row) => String(row.status || "").toLowerCase() === "posted").length;
    const approved = filteredTxnRows.filter((row) => String(row.status || "").toLowerCase() === "approved").length;
    const draft = filteredTxnRows.filter((row) => String(row.status || "").toLowerCase() === "draft").length;
    return { posted, approved, draft, count: filteredTxnRows.length };
  }, [filteredTxnRows]);

  const resetRule = () => {
    setRuleForm(defaultRule);
  };

  const resetTxn = () => {
    setTxnForm(defaultTxn);
  };

  const saveRule = async () => {
    const payload = {
      ...ruleForm,
      approval_required: ruleForm.approval_required ? 1 : 0,
      auto_apply: ruleForm.auto_apply ? 1 : 0,
      is_active: ruleForm.is_active ? 1 : 0,
      name: ruleForm.name.trim(),
      fuel_type: ruleForm.fuel_type || null,
      min_amount: ruleForm.min_amount || 0,
      min_volume: ruleForm.min_volume || 0,
      max_discount_amount: ruleForm.max_discount_amount || null,
    };

    if (!payload.name) {
      showNotice("warning", "Rule name required", "Please enter a discount rule name.");
      return;
    }
    if (!payload.discount_value || Number(payload.discount_value) <= 0) {
      showNotice("warning", "Value required", "Please enter a valid discount value.");
      return;
    }
    if (payload.discount_type === "percent" && Number(payload.discount_value) > 100) {
      showNotice("warning", "Percent invalid", "Percent discount cannot exceed 100.");
      return;
    }
    if (payload.min_amount && Number(payload.min_amount) < 0) {
      showNotice("warning", "Min amount invalid", "Minimum amount cannot be negative.");
      return;
    }
    if (payload.min_volume && Number(payload.min_volume) < 0) {
      showNotice("warning", "Min volume invalid", "Minimum volume cannot be negative.");
      return;
    }
    if (payload.max_discount_amount && Number(payload.max_discount_amount) < 0) {
      showNotice("warning", "Max discount invalid", "Maximum discount cannot be negative.");
      return;
    }
    if (payload.start_date && payload.end_date && payload.end_date < payload.start_date) {
      showNotice("warning", "Date range invalid", "End date cannot be earlier than start date.");
      return;
    }

    setSavingRule(true);
    try {
      if (ruleForm.id) {
        await api.put(`/api/discounts/rules/${ruleForm.id}`, payload, { headers });
        showNotice("success", "Rule updated", "The discount rule has been saved.");
      } else {
        await api.post("/api/discounts/rules", payload, { headers });
        showNotice("success", "Rule saved", "The discount rule is ready.");
      }
      resetRule();
      await fetchRules();
    } catch (err) {
      showNotice("error", "Save failed", err?.response?.data?.error || "Unable to save discount rule.");
    } finally {
      setSavingRule(false);
    }
  };

  const editRule = (row) => {
    setTab("rules");
    setRuleForm({
      ...row,
      approval_required: !!Number(row.approval_required),
      auto_apply: !!Number(row.auto_apply),
      is_active: !!Number(row.is_active),
      start_date: row.start_date ? row.start_date.slice(0, 10) : "",
      end_date: row.end_date ? row.end_date.slice(0, 10) : "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deleteRule = async () => {
    if (!deleteTarget || deleteTarget.kind !== "rule") return;
    try {
      await api.delete(`/api/discounts/rules/${deleteTarget.id}`, { headers });
      showNotice("success", "Rule deleted", "The discount rule has been removed.");
      await fetchRules();
    } catch (err) {
      showNotice("error", "Delete failed", err?.response?.data?.error || "Unable to delete discount rule.");
    } finally {
      setDeleteTarget(null);
    }
  };

  const saveTransaction = async () => {
    const gross = Number(txnForm.gross_amount || 0);
    const quantity = Number(txnForm.quantity || 0);
    let discountAmount = Number(txnForm.discount_amount || 0);
    const hasRule = !!txnForm.rule_id;
    const selectedRule = currentRule;

    if (!txnForm.discount_time) {
      showNotice("warning", "DateTime required", "Please choose a discount date and time.");
      return;
    }
    if (!txnForm.customer_name.trim()) {
      showNotice("warning", "Customer required", "Please enter the customer name.");
      return;
    }
    if (!txnForm.vehicle_no.trim()) {
      showNotice("warning", "Vehicle required", "Please enter the vehicle number.");
      return;
    }
    if (!txnForm.product_type.trim()) {
      showNotice("warning", "Product required", "Please enter the product type.");
      return;
    }
    if (!gross || gross <= 0) {
      showNotice("warning", "Gross required", "Please enter a valid gross amount.");
      return;
    }
    if (quantity < 0) {
      showNotice("warning", "Quantity invalid", "Quantity cannot be negative.");
      return;
    }

    if (hasRule && selectedRule) {
      discountAmount = discountFormula(selectedRule, gross, quantity);
      if (Number(selectedRule.approval_required || 0) === 1 && !txnForm.approval_note.trim()) {
        showNotice("warning", "Approval note required", "This rule needs an approval note before posting.");
        return;
      }
      if (selectedRule.discount_type === "percent" && Number(selectedRule.discount_value || 0) > 100) {
        showNotice("warning", "Rule invalid", "Selected rule has an invalid percent discount.");
        return;
      }
    } else if (!discountAmount || discountAmount < 0) {
      showNotice("warning", "Discount required", "Please enter a valid discount amount.");
      return;
    }

    if (discountAmount > gross) {
      showNotice("warning", "Discount too high", "Discount cannot be greater than gross amount.");
      return;
    }

    const netAmount = Math.max(0, gross - discountAmount);
    const dt = parseSafeDate(txnForm.discount_time);
    if (!dt || !dt.isValid()) {
      showNotice("warning", "DateTime invalid", "Please enter a valid discount date and time.");
      return;
    }

    const payload = {
      ...txnForm,
      rule_id: txnForm.rule_id || null,
      reconciliation_id: txnForm.reconciliation_id || null,
      operator_id: txnForm.operator_id || null,
      approved_by: txnForm.approved_by || null,
      discount_amount: Number(discountAmount.toFixed(2)),
      gross_amount: Number(gross.toFixed(2)),
      net_amount: Number(netAmount.toFixed(2)),
      customer_name: txnForm.customer_name.trim(),
      vehicle_no: txnForm.vehicle_no.trim(),
      product_type: txnForm.product_type.trim(),
      approval_note: txnForm.approval_note || null,
      remark: txnForm.remark || null,
      discount_time: dt.format("YYYY-MM-DDTHH:mm"),
      quantity: Number(quantity.toFixed(3)),
    };

    setSavingTxn(true);
    try {
      if (txnForm.id) {
        await api.put(`/api/discounts/transactions/${txnForm.id}`, payload, { headers });
        showNotice("success", "Entry updated", "The discount entry has been saved.");
      } else {
        await api.post("/api/discounts/transactions", payload, { headers });
        showNotice("success", "Entry saved", "The discount entry is ready.");
      }
      resetTxn();
      await fetchTransactions(filters);
    } catch (err) {
      showNotice("error", "Save failed", err?.response?.data?.error || "Unable to save discount entry.");
    } finally {
      setSavingTxn(false);
    }
  };

  const editTxn = (row) => {
    setTab("transactions");
    setTxnForm({
      ...row,
      rule_id: row.rule_id || "",
      reconciliation_id: row.reconciliation_id || "",
      operator_id: row.operator_id || "",
      approved_by: row.approved_by || "",
      approval_note: row.approval_note || "",
      discount_time: row.discount_time ? moment(row.discount_time).format("YYYY-MM-DDTHH:mm") : indiaNow().format("YYYY-MM-DDTHH:mm"),
      gross_amount: row.gross_amount,
      discount_amount: row.discount_amount,
      quantity: row.quantity,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deleteTxn = async () => {
    if (!deleteTarget || deleteTarget.kind !== "txn") return;
    try {
      await api.delete(`/api/discounts/transactions/${deleteTarget.id}`, { headers });
      showNotice("success", "Entry deleted", "The discount entry has been removed.");
      await fetchTransactions(filters);
    } catch (err) {
      showNotice("error", "Delete failed", err?.response?.data?.error || "Unable to delete discount entry.");
    } finally {
      setDeleteTarget(null);
    }
  };

  const exportTransactions = () => {
    const exportData = filteredTxnRows.map((r) => ({
      DateTime: moment(r.discount_time).tz("Asia/Kolkata").format("DD-MM-YYYY hh:mm A"),
      Rule: r.rule_name || "Manual",
      Operator: r.operator_name || "",
      Customer: r.customer_name || "",
      Vehicle: r.vehicle_no || "",
      Product: r.product_type || "",
      Quantity: r.quantity,
      Gross: r.gross_amount,
      Discount: r.discount_amount,
      Net: r.net_amount,
      Status: r.status,
      Remark: r.remark || "",
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Discounts");
    const buffer = XLSX.write(wb, { type: "array", bookType: "xlsx" });
    saveAs(new Blob([buffer]), "discount_transactions.xlsx");
  };

  const applyTxnFilters = () => {
    const next = {
      startDate: txnRange.startDate,
      endDate: txnRange.endDate,
      operator_id: txnOperatorFilter,
      status: txnStatusFilter,
      search: txnSearch,
    };
    setFilters(next);
    fetchTransactions(next);
  };

  const resetTxnFilters = () => {
    const next = { ...defaultRange, operator_id: "", status: "", search: "" };
    setTxnRange(defaultRange);
    setTxnOperatorFilter("");
    setTxnStatusFilter("");
    setTxnSearch("");
    setFilters(next);
    fetchTransactions(next);
  };

  const quickRange = (days) => {
    const next = {
      startDate: days === 1 ? indiaNow().startOf("day").format("YYYY-MM-DDT00:00") : indiaNow().subtract(days - 1, "days").startOf("day").format("YYYY-MM-DDT00:00"),
      endDate: indiaNow().endOf("day").format("YYYY-MM-DDT23:59"),
      operator_id: txnOperatorFilter,
      status: txnStatusFilter,
      search: txnSearch,
    };
    setTxnRange({ startDate: next.startDate, endDate: next.endDate });
    setFilters(next);
    fetchTransactions(next);
  };

  const selectedRulePreview = useMemo(() => {
    if (!currentRule) return null;
    const gross = Number(txnForm.gross_amount || 0);
    const quantity = Number(txnForm.quantity || 0);
    const amount = discountFormula(currentRule, gross, quantity);
    return {
      amount,
      net: Math.max(0, gross - amount),
      label:
        currentRule.discount_type === "percent"
          ? `${Number(currentRule.discount_value)}%`
          : currentRule.discount_type === "per_liter"
          ? `${money(currentRule.discount_value)} / litre`
          : money(currentRule.discount_value),
    };
  }, [currentRule, txnForm.gross_amount, txnForm.quantity]);

  const productScopes = [
    { value: "fuel", label: "Fuel" },
    { value: "lube", label: "Lube" },
    { value: "all", label: "All" },
  ];

  const discountTypes = [
    { value: "flat", label: "Flat" },
    { value: "percent", label: "Percent" },
    { value: "per_liter", label: "Per litre" },
  ];

  const statusOptions = [
    { value: "draft", label: "Draft" },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
    { value: "posted", label: "Posted" },
  ];

  return (
    <div className="cc-page text-light discount-page">
      <NotificationToast notice={notice} onClose={() => setNotice(null)} />
      <ConfirmModal
        open={!!deleteTarget}
        title={deleteTarget?.kind === "rule" ? "Delete discount rule?" : "Delete discount entry?"}
        message={
          deleteTarget?.kind === "rule"
            ? "This will remove the rule from the master list."
            : "This will remove the selected discount transaction."
        }
        onCancel={() => setDeleteTarget(null)}
        onConfirm={deleteTarget?.kind === "rule" ? deleteRule : deleteTxn}
      />

      <section className="cc-hero">
        <div>
          <div className="cc-eyebrow">Discount Management</div>
          <h2>Discounts that feel calmer, clearer, and easier to trust.</h2>
          <p>
            Use a premium rule flow, keep the transaction register clean, and let the system help with
            discount math instead of forcing everyone to work it out by hand.
          </p>
          <div className="cc-hero-meta">
            <span>{rules.length} rules</span>
            <span>{filteredTxnRows.length} entries</span>
            <span>{money(totals.discount)} total discount</span>
          </div>
        </div>

        <div className="cc-hero-actions">
          <button type="button" className="btn cc-secondary-action" onClick={() => { fetchRules(); fetchTransactions(filters); }} disabled={loadingRules || loadingTxn}>
            <FaRedoAlt aria-hidden="true" />
            Refresh
          </button>
          <button type="button" className="btn cc-primary-action" onClick={() => setTab(tab === "rules" ? "transactions" : "rules")}>
            {tab === "rules" ? "Go to entries" : "Back to rules"}
          </button>
        </div>
      </section>

      <section className="cc-summary-grid">
        <StatCard tone="blue" icon={FaTags} label="Rules" value={String(rules.length)} caption={`${ruleSummary.active} active`} />
        <StatCard tone="green" icon={FaWallet} label="Entries" value={String(filteredTxnRows.length)} caption={`${txnSummary.posted} posted`} />
        <StatCard tone="amber" icon={FaLayerGroup} label="Total gross" value={money(totals.gross)} caption={money(totals.net)} />
        <StatCard tone="slate" icon={FaCheckCircle} label="Auto / approval" value={`${ruleSummary.autoApply}/${ruleSummary.approvalRequired}`} caption="Auto-apply / approval-required" />
      </section>

      <div className="cc-tabs" role="tablist" aria-label="Discount views">
        <button type="button" className={`cc-tab-btn ${tab === "rules" ? "active" : ""}`} onClick={() => setTab("rules")}>
          Rules
        </button>
        <button type="button" className={`cc-tab-btn ${tab === "transactions" ? "active" : ""}`} onClick={() => setTab("transactions")}>
          Entries
        </button>
      </div>

      {tab === "rules" ? (
        <>
          <section className="cc-context-strip">
            <div className="cc-context-head">
              <div>
                <span>Rule context</span>
                <h3>Search and filter before opening the rule list</h3>
              </div>
              <div className="cc-context-chip">
                <strong>{ruleStatusFilter === "all" ? "All statuses" : ruleStatusFilter}</strong>
                <span>{filteredRuleCount} rules in view</span>
              </div>
            </div>

            <div className="discount-filter-grid">
              <div className="cc-search-shell">
                <FaSearch aria-hidden="true" />
                <input
                  className="form-control"
                  placeholder="Search rule or fuel type"
                  value={ruleSearch}
                  onChange={(e) => setRuleSearch(e.target.value)}
                />
              </div>
              <select className="form-select" value={ruleScopeFilter} onChange={(e) => setRuleScopeFilter(e.target.value)}>
                <option value="">All scopes</option>
                {productScopes.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <select className="form-select" value={ruleStatusFilter} onChange={(e) => setRuleStatusFilter(e.target.value)}>
                <option value="all">All statuses</option>
                <option value="active">Active only</option>
                <option value="inactive">Inactive only</option>
              </select>
            </div>
          </section>

          <section className="cc-grid discount-grid">
            <div className="cc-card cc-form-card">
              <div className="cc-card-head">
                <div>
                  <span>Discount rule</span>
                  <h3>{ruleForm.id ? "Edit a rule" : "Create a new discount rule"}</h3>
                </div>
                <div className="cc-ledger-chip">
                  <strong>{ruleForm.discount_type}</strong>
                  <span>{ruleForm.product_scope}</span>
                </div>
              </div>

              <div className="cc-form-grid discount-form-grid">
                <div>
                  <label className="form-label">Rule name</label>
                  <input className="form-control" value={ruleForm.name} onChange={(e) => setRuleForm({ ...ruleForm, name: e.target.value })} placeholder="Weekend pump discount" />
                </div>
                <div>
                  <label className="form-label">Scope</label>
                  <select className="form-select" value={ruleForm.product_scope} onChange={(e) => setRuleForm({ ...ruleForm, product_scope: e.target.value })}>
                    {productScopes.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="form-label">Fuel type</label>
                  <select className="form-select" value={ruleForm.fuel_type} onChange={(e) => setRuleForm({ ...ruleForm, fuel_type: e.target.value })}>
                    <option value="">All</option>
                    <option value="Petrol">Petrol</option>
                    <option value="Diesel">Diesel</option>
                    <option value="Power Petrol">Power Petrol</option>
                    <option value="Power Diesel">Power Diesel</option>
                    <option value="CNG">CNG</option>
                    <option value="Diesel 100">Diesel 100</option>
                  </select>
                </div>
                <div>
                  <label className="form-label">Type</label>
                  <select className="form-select" value={ruleForm.discount_type} onChange={(e) => setRuleForm({ ...ruleForm, discount_type: e.target.value })}>
                    {discountTypes.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="form-label">Value</label>
                  <input type="number" step="0.01" className="form-control" value={ruleForm.discount_value} onChange={(e) => setRuleForm({ ...ruleForm, discount_value: e.target.value })} />
                </div>
                <div>
                  <label className="form-label">Min amount</label>
                  <input type="number" className="form-control" value={ruleForm.min_amount} onChange={(e) => setRuleForm({ ...ruleForm, min_amount: e.target.value })} />
                </div>
                <div>
                  <label className="form-label">Min volume</label>
                  <input type="number" className="form-control" value={ruleForm.min_volume} onChange={(e) => setRuleForm({ ...ruleForm, min_volume: e.target.value })} />
                </div>
                <div>
                  <label className="form-label">Max discount</label>
                  <input type="number" className="form-control" value={ruleForm.max_discount_amount} onChange={(e) => setRuleForm({ ...ruleForm, max_discount_amount: e.target.value })} />
                </div>
                <div>
                  <label className="form-label">Start date</label>
                  <input type="date" className="form-control" value={ruleForm.start_date} onChange={(e) => setRuleForm({ ...ruleForm, start_date: e.target.value })} />
                </div>
                <div>
                  <label className="form-label">End date</label>
                  <input type="date" className="form-control" value={ruleForm.end_date} onChange={(e) => setRuleForm({ ...ruleForm, end_date: e.target.value })} />
                </div>
                <div className="discount-switch-row">
                  <label className="form-check">
                    <input className="form-check-input" type="checkbox" checked={ruleForm.approval_required} onChange={(e) => setRuleForm({ ...ruleForm, approval_required: e.target.checked })} />
                    <span>Approval</span>
                  </label>
                  <label className="form-check">
                    <input className="form-check-input" type="checkbox" checked={ruleForm.auto_apply} onChange={(e) => setRuleForm({ ...ruleForm, auto_apply: e.target.checked })} />
                    <span>Auto apply</span>
                  </label>
                  <label className="form-check">
                    <input className="form-check-input" type="checkbox" checked={ruleForm.is_active} onChange={(e) => setRuleForm({ ...ruleForm, is_active: e.target.checked })} />
                    <span>Active</span>
                  </label>
                </div>
              </div>

              <div className="cc-action-row">
                <button type="button" className="btn cc-primary-action" onClick={saveRule} disabled={savingRule}>
                  <FaSave aria-hidden="true" />
                  {savingRule ? "Saving..." : ruleForm.id ? "Update rule" : "Save rule"}
                </button>
                {ruleForm.id && (
                  <button type="button" className="btn btn-light" onClick={resetRule}>
                    Cancel edit
                  </button>
                )}
              </div>
            </div>

            <div className="cc-card cc-form-card">
              <div className="cc-card-head">
                <div>
                  <span>Rule insights</span>
                  <h3>Keep the discount logic small and readable</h3>
                </div>
              </div>
              <div className="discount-insight-stack">
                <div className="discount-insight-note">
                  <strong>Percent rules</strong>
                  <span>Percent values are capped at 100 and validation blocks invalid ranges.</span>
                </div>
                <div className="discount-insight-note">
                  <strong>Auto apply</strong>
                  <span>When a rule is selected in entries, the discount amount is calculated automatically.</span>
                </div>
                <div className="discount-insight-note">
                  <strong>Approval note</strong>
                  <span>If approval is required, posting a transaction needs a note before saving.</span>
                </div>
              </div>
            </div>
          </section>

          <section className="cc-card cc-report-card">
            <div className="cc-card-head">
              <div>
                <span>Rule register</span>
                <h3>White table header and compact actions</h3>
              </div>
              <div className="cc-table-meta">
                <span>{filteredRuleCount} rows</span>
                <strong>{safeRulePage}/{ruleTotalPages}</strong>
              </div>
            </div>

            <div className="table-responsive cc-table-wrap">
              <table className="table cc-table">
                <thead>
                  <tr>
                    <th style={{ color: "#fff", background: "#0f172a" }}>Name</th>
                    <th style={{ color: "#fff", background: "#0f172a" }}>Scope</th>
                    <th style={{ color: "#fff", background: "#0f172a" }}>Fuel type</th>
                    <th style={{ color: "#fff", background: "#0f172a" }}>Type</th>
                    <th style={{ color: "#fff", background: "#0f172a" }}>Value</th>
                    <th style={{ color: "#fff", background: "#0f172a" }}>Approval</th>
                    <th style={{ color: "#fff", background: "#0f172a" }}>Auto</th>
                    <th style={{ color: "#fff", background: "#0f172a" }}>Active</th>
                    <th style={{ width: 150, color: "#fff", background: "#0f172a" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingRules ? (
                    <tr>
                      <td colSpan="9">
                        <div className="cc-empty-state">
                          <FaTags aria-hidden="true" />
                          <strong>Loading rules</strong>
                          <span>Please wait while the discount rules are fetched.</span>
                        </div>
                      </td>
                    </tr>
                  ) : ruleRows.length ? (
                    ruleRows.map((row) => (
                      <tr key={row.id}>
                        <td data-label="Name">
                          <strong>{row.name}</strong>
                          <span>{row.product_scope || "-"}</span>
                        </td>
                        <td data-label="Scope">{row.product_scope}</td>
                        <td data-label="Fuel type">{row.fuel_type || "All"}</td>
                        <td data-label="Type">{row.discount_type}</td>
                        <td data-label="Value">{row.discount_type === "percent" ? `${row.discount_value}%` : money(row.discount_value)}</td>
                        <td data-label="Approval">{Number(row.approval_required) ? "Yes" : "No"}</td>
                        <td data-label="Auto">{Number(row.auto_apply) ? "Yes" : "No"}</td>
                        <td data-label="Active">
                          <span className={`cc-pill ${Number(row.is_active) ? "paid" : "pending"}`}>
                            {Number(row.is_active) ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td data-label="Actions" className="cc-row-actions discount-actions">
                          <button type="button" className="btn btn-sm btn-light cc-mini-btn" onClick={() => editRule(row)}>
                            Edit
                          </button>
                          <button type="button" className="btn btn-sm btn-danger cc-mini-btn" onClick={() => setDeleteTarget({ kind: "rule", id: row.id })}>
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9">
                        <div className="cc-empty-state">
                          <FaTags aria-hidden="true" />
                          <strong>No discount rules found</strong>
                          <span>Try another search or create the first discount rule.</span>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {filteredRuleCount > pageSize && (
              <div className="cc-pagination">
                <button type="button" className="cc-page-btn" disabled={safeRulePage === 1} onClick={() => setRulePage((p) => p - 1)}>
                  Prev
                </button>
                <div className="cc-page-list">
                  {getPageNumbers(safeRulePage, ruleTotalPages).map((pageNo) => (
                    <button key={pageNo} type="button" className={`cc-page-btn ${safeRulePage === pageNo ? "active" : ""}`} onClick={() => setRulePage(pageNo)}>
                      {pageNo}
                    </button>
                  ))}
                </div>
                <button type="button" className="cc-page-btn" disabled={safeRulePage === ruleTotalPages} onClick={() => setRulePage((p) => p + 1)}>
                  Next
                </button>
              </div>
            )}
          </section>
        </>
      ) : (
        <>
          <section className="cc-context-strip">
            <div className="cc-context-head">
              <div>
                <span>Entry context</span>
                <h3>Pick the date range and filters before reading the register</h3>
              </div>
              <div className="cc-context-chip">
                <strong>{txnSummary.count} entries</strong>
                <span>{moment(txnRange.startDate).format("DD MMM")} to {moment(txnRange.endDate).format("DD MMM")}</span>
              </div>
            </div>

            <div className="discount-filter-grid">
              <div>
                <label className="form-label">Start</label>
                <input className="form-control" type="datetime-local" value={txnRange.startDate} onChange={(e) => setTxnRange((prev) => ({ ...prev, startDate: e.target.value }))} />
              </div>
              <div>
                <label className="form-label">End</label>
                <input className="form-control" type="datetime-local" value={txnRange.endDate} onChange={(e) => setTxnRange((prev) => ({ ...prev, endDate: e.target.value }))} />
              </div>
              <div>
                <label className="form-label">Operator</label>
                <select className="form-select" value={txnOperatorFilter} onChange={(e) => setTxnOperatorFilter(e.target.value)}>
                  <option value="">All</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name || u.username || `User ${u.id}`}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="form-label">Status</label>
                <select className="form-select" value={txnStatusFilter} onChange={(e) => setTxnStatusFilter(e.target.value)}>
                  <option value="">All</option>
                  {statusOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="form-label">Search</label>
                <div className="cc-search-shell">
                  <FaSearch aria-hidden="true" />
                  <input className="form-control" value={txnSearch} onChange={(e) => setTxnSearch(e.target.value)} placeholder="Search customer, vehicle, product, rule" />
                </div>
              </div>
            </div>

            <div className="cc-passbook-toolbar">
              <button type="button" className="btn btn-sm btn-light cc-mini-btn" onClick={() => quickRange(1)}>1D</button>
              <button type="button" className="btn btn-sm btn-light cc-mini-btn" onClick={() => quickRange(7)}>1W</button>
              <button type="button" className="btn btn-sm btn-light cc-mini-btn" onClick={() => quickRange(14)}>2W</button>
              <button type="button" className="btn btn-sm btn-light cc-mini-btn" onClick={() => quickRange(30)}>30D</button>
              <button type="button" className="btn btn-sm btn-success" onClick={applyTxnFilters}>Apply filters</button>
              <button type="button" className="btn btn-sm btn-light" onClick={resetTxnFilters}>Reset</button>
              <button type="button" className="btn btn-sm btn-light" onClick={exportTransactions}>
                <FaFileExport aria-hidden="true" /> Export
              </button>
            </div>
          </section>

          <section className="cc-summary-grid">
            <StatCard tone="blue" icon={FaLayerGroup} label="Total gross" value={money(totals.gross)} caption={`${txnSummary.posted} posted`} />
            <StatCard tone="green" icon={FaWallet} label="Total discount" value={money(totals.discount)} caption={`${txnSummary.approved} approved`} />
            <StatCard tone="amber" icon={FaCheckCircle} label="Total net" value={money(totals.net)} caption={`${txnSummary.draft} draft`} />
            <StatCard tone="slate" icon={FaTags} label="Current rule" value={currentRule ? currentRule.name : "Manual"} caption={selectedRulePreview ? selectedRulePreview.label : "No rule selected"} />
          </section>

          <section className="cc-grid discount-grid">
            <div className="cc-card cc-form-card">
              <div className="cc-card-head">
                <div>
                  <span>Discount entry</span>
                  <h3>{txnForm.id ? "Edit a discount entry" : "Create a new discount entry"}</h3>
                </div>
                <div className="cc-ledger-chip">
                  <strong>{txnForm.status}</strong>
                  <span>{txnForm.rule_id ? "Rule-linked" : "Manual"}</span>
                </div>
              </div>

              <div className="cc-form-grid discount-form-grid">
                <div>
                  <label className="form-label">Rule</label>
                  <select
                    className="form-select"
                    value={txnForm.rule_id}
                    onChange={(e) => {
                      const ruleId = e.target.value;
                      const rule = rules.find((r) => String(r.id) === String(ruleId)) || null;
                      const gross = Number(txnForm.gross_amount || 0);
                      const qty = Number(txnForm.quantity || 0);
                      const autoDiscount = rule ? discountFormula(rule, gross, qty) : Number(txnForm.discount_amount || 0);
                      setTxnForm((prev) => ({
                        ...prev,
                        rule_id: ruleId,
                        discount_amount: rule ? String(Number(autoDiscount.toFixed(2))) : prev.discount_amount,
                      }));
                    }}
                  >
                    <option value="">Manual</option>
                    {rules.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="form-label">Operator</label>
                  <select className="form-select" value={txnForm.operator_id} onChange={(e) => setTxnForm({ ...txnForm, operator_id: e.target.value })}>
                    <option value="">Select</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>{u.name || u.username || `User ${u.id}`}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="form-label">Customer</label>
                  <input className="form-control" value={txnForm.customer_name} onChange={(e) => setTxnForm({ ...txnForm, customer_name: e.target.value })} />
                </div>
                <div>
                  <label className="form-label">Vehicle no</label>
                  <input className="form-control" value={txnForm.vehicle_no} onChange={(e) => setTxnForm({ ...txnForm, vehicle_no: e.target.value })} />
                </div>
                <div>
                  <label className="form-label">Product</label>
                  <input className="form-control" value={txnForm.product_type} onChange={(e) => setTxnForm({ ...txnForm, product_type: e.target.value })} />
                </div>
                <div>
                  <label className="form-label">Quantity</label>
                  <input type="number" step="0.001" className="form-control" value={txnForm.quantity} onChange={(e) => setTxnForm({ ...txnForm, quantity: e.target.value })} />
                </div>
                <div>
                  <label className="form-label">Gross amount</label>
                  <input type="number" step="0.01" className="form-control" value={txnForm.gross_amount} onChange={(e) => setTxnForm((prev) => {
                    const next = { ...prev, gross_amount: e.target.value };
                    const rule = rules.find((r) => String(r.id) === String(prev.rule_id)) || null;
                    if (rule) next.discount_amount = String(Number(discountFormula(rule, Number(e.target.value || 0), Number(prev.quantity || 0)).toFixed(2)));
                    return next;
                  })} />
                </div>
                <div>
                  <label className="form-label">Discount amount</label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-control"
                    value={txnForm.discount_amount}
                    onChange={(e) => setTxnForm({ ...txnForm, discount_amount: e.target.value })}
                    disabled={!!currentRule}
                  />
                </div>
                <div>
                  <label className="form-label">Status</label>
                  <select className="form-select" value={txnForm.status} onChange={(e) => setTxnForm({ ...txnForm, status: e.target.value })}>
                    {statusOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="form-label">DateTime</label>
                  <input type="datetime-local" className="form-control" value={txnForm.discount_time} onChange={(e) => setTxnForm({ ...txnForm, discount_time: e.target.value })} />
                </div>
                <div className="discount-full">
                  <label className="form-label">Approval note</label>
                  <input className="form-control" value={txnForm.approval_note} onChange={(e) => setTxnForm({ ...txnForm, approval_note: e.target.value })} placeholder="Required if rule needs approval" />
                </div>
                <div className="discount-full">
                  <label className="form-label">Remark</label>
                  <input className="form-control" value={txnForm.remark} onChange={(e) => setTxnForm({ ...txnForm, remark: e.target.value })} placeholder="Optional note" />
                </div>
              </div>

              <div className="discount-preview">
                <div>
                  <span>Suggested discount</span>
                  <strong>{selectedRulePreview ? money(selectedRulePreview.amount) : money(txnForm.discount_amount)}</strong>
                </div>
                <div>
                  <span>Expected net</span>
                  <strong>{selectedRulePreview ? money(selectedRulePreview.net) : money(Number(txnForm.gross_amount || 0) - Number(txnForm.discount_amount || 0))}</strong>
                </div>
                <div>
                  <span>Rule source</span>
                  <strong>{currentRule ? currentRule.name : "Manual"}</strong>
                </div>
              </div>

              <div className="cc-action-row">
                <button type="button" className="btn cc-primary-action" onClick={saveTransaction} disabled={savingTxn}>
                  <FaSave aria-hidden="true" />
                  {savingTxn ? "Saving..." : txnForm.id ? "Update entry" : "Save entry"}
                </button>
                {txnForm.id && (
                  <button type="button" className="btn btn-light" onClick={resetTxn}>
                    Cancel edit
                  </button>
                )}
              </div>
            </div>

            <div className="cc-card cc-form-card">
              <div className="cc-card-head">
                <div>
                  <span>Entry insights</span>
                  <h3>See how the discount math is shaping up</h3>
                </div>
              </div>
              <div className="discount-insight-stack">
                <div className="discount-insight-note">
                  <strong>Rule math</strong>
                  <span>If a rule is selected, the discount amount is computed from gross and quantity and capped by max discount.</span>
                </div>
                <div className="discount-insight-note">
                  <strong>Validation</strong>
                  <span>Gross must be positive, discount cannot exceed gross, and approval rules need a note.</span>
                </div>
                <div className="discount-insight-note">
                  <strong>Manual fallback</strong>
                  <span>If no rule is selected, you can enter a manual discount amount.</span>
                </div>
              </div>
            </div>
          </section>

          <section className="cc-card cc-report-card">
            <div className="cc-card-head">
              <div>
                <span>Discount entries</span>
                <h3>Readable register with white headers and paged rows</h3>
              </div>
              <div className="cc-table-meta">
                <span>{filteredTxnRows.length} rows</span>
                <strong>{safeTxnPage}/{txnTotalPages}</strong>
              </div>
            </div>

            <div className="table-responsive cc-table-wrap">
              <table className="table cc-table">
                <thead>
                  <tr>
                    <th style={{ color: "#fff", background: "#0f172a" }}>Date</th>
                    <th style={{ color: "#fff", background: "#0f172a" }}>Rule</th>
                    <th style={{ color: "#fff", background: "#0f172a" }}>Operator</th>
                    <th style={{ color: "#fff", background: "#0f172a" }}>Customer</th>
                    <th style={{ color: "#fff", background: "#0f172a" }}>Vehicle</th>
                    <th style={{ color: "#fff", background: "#0f172a" }}>Gross</th>
                    <th style={{ color: "#fff", background: "#0f172a" }}>Discount</th>
                    <th style={{ color: "#fff", background: "#0f172a" }}>Net</th>
                    <th style={{ color: "#fff", background: "#0f172a" }}>Status</th>
                    <th style={{ width: 150, color: "#fff", background: "#0f172a" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingTxn ? (
                    <tr>
                      <td colSpan="10">
                        <div className="cc-empty-state">
                          <FaTags aria-hidden="true" />
                          <strong>Loading entries</strong>
                          <span>Please wait while the discount register is fetched.</span>
                        </div>
                      </td>
                    </tr>
                  ) : txnRows.length ? (
                    txnRows.map((r) => (
                      <tr key={r.id}>
                        <td data-label="Date">{moment(r.discount_time).tz("Asia/Kolkata").format("DD-MM-YYYY hh:mm A")}</td>
                        <td data-label="Rule">
                          <strong>{r.rule_name || "Manual"}</strong>
                          <span>{r.status || "-"}</span>
                        </td>
                        <td data-label="Operator">{r.operator_name || "-"}</td>
                        <td data-label="Customer">{r.customer_name || "-"}</td>
                        <td data-label="Vehicle">{r.vehicle_no || "-"}</td>
                        <td data-label="Gross">{money(r.gross_amount)}</td>
                        <td data-label="Discount">{money(r.discount_amount)}</td>
                        <td data-label="Net">{money(r.net_amount)}</td>
                        <td data-label="Status">
                          <span className={`cc-pill ${String(r.status || "").toLowerCase()}`}>{r.status || "-"}</span>
                        </td>
                        <td data-label="Actions" className="cc-row-actions discount-actions">
                          <button type="button" className="btn btn-sm btn-light cc-mini-btn" onClick={() => editTxn(r)}>
                            Edit
                          </button>
                          <button type="button" className="btn btn-sm btn-danger cc-mini-btn" onClick={() => setDeleteTarget({ kind: "txn", id: r.id })}>
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="10">
                        <div className="cc-empty-state">
                          <FaTags aria-hidden="true" />
                          <strong>No discount entries found</strong>
                          <span>Try another date range or create the first entry.</span>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {filteredTxnRows.length > pageSize && (
              <div className="cc-pagination">
                <button type="button" className="cc-page-btn" disabled={safeTxnPage === 1} onClick={() => setTxnPage((p) => p - 1)}>
                  Prev
                </button>
                <div className="cc-page-list">
                  {getPageNumbers(safeTxnPage, txnTotalPages).map((pageNo) => (
                    <button key={pageNo} type="button" className={`cc-page-btn ${safeTxnPage === pageNo ? "active" : ""}`} onClick={() => setTxnPage(pageNo)}>
                      {pageNo}
                    </button>
                  ))}
                </div>
                <button type="button" className="cc-page-btn" disabled={safeTxnPage === txnTotalPages} onClick={() => setTxnPage((p) => p + 1)}>
                  Next
                </button>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
